import React, { useEffect, useMemo, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { getProjectsRoster } from '../../services/api';
import { useParticipant } from '../../hooks/useParticipant';
import { useAsync } from '../aai/useAsync';
import type { AaiRequest, QuickWin, QuickWinList } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, FilterChips, ListRow, Loading,
  PageBody, PageHead, Panel, PanelHead, RouteMark, StatusChip, Tabs,
} from '../../components/aa';
import type { RowMeta } from '../../components/aa';
import { QuickWinDrawer, RequestDrawer } from './WorkDrawers';
import { nextFor, stateFor } from '../partner/work';
import { MySkillsSection } from './MySkills';
import ClientAgentsPage from '../client/ClientAgentsPage';
import { ClientRequests, notBuiltYet } from '../provider/ClientRequests';

/*
 * My Projects.
 *
 * Every approved piece of work is a Project. A Quick Win is a focused result
 * used and reviewed here in the portal; a Delivery Project is scoped work with
 * a named team. Both kinds live on this one page, the same shape the
 * Department Head, Champion and Owner read, so nobody learns two layouts.
 *
 * What an employee sees is server-scoped: the Quick Wins approved for them or
 * their department, and the requests they raised or were named on. Delivery
 * detail lives in Workspace and is not opened from here; seeing a summary and
 * holding Workspace membership are different permissions.
 */

interface RequestList {
  scope: string;
  requests: AaiRequest[];
  // Work named to you in another company — a delivery expert's client work. It
  // arrives separately so it never counts as your own company's work anywhere
  // else, and this page, which means "mine", puts it back alongside.
  assigned_elsewhere?: AaiRequest[];
}

type WorkFilter = 'active' | 'needs_me' | 'in_progress' | 'waiting' | 'ready' | 'all';

const FILTERS: Array<{ id: WorkFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'needs_me', label: 'Needs me' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'ready', label: 'Ready to use' },
  { id: 'all', label: 'All' },
];

// Each filter is a predicate over the real request states, so a tab can never
// show a row whose state does not match its label.
const MATCH: Record<WorkFilter, (r: AaiRequest) => boolean> = {
  active: (r) => r.status !== 'declined',
  needs_me: (r) => r.status === 'needs_info',
  in_progress: (r) => r.status === 'approved' && !r.tool_skill_id,
  waiting: (r) => r.status === 'pending' || r.status === 'dept_approved',
  ready: (r) => Boolean(r.tool_skill_id),
  all: () => true,
};

const ownerOf = (r: AaiRequest): string => {
  const named = r.assigned_expert_email || r.owner_email || r.decided_by;
  if (!named) return 'Not yet named';
  return named;
};

const isDelivery = (r: AaiRequest): boolean => r.status !== 'declined' && !r.tool_skill_id;

const QuickWinRow: React.FC<{ item: QuickWin; onOpen: () => void }> = ({ item, onOpen }) => {
  let right = <StatusChip status="ready_short" />;
  if (!item.ready) right = <Chip tone="warn">{item.setup_label}</Chip>;
  return (
    <ListRow
      mark={<RouteMark kind="quick_win" size="lg" />}
      title={item.name}
      desc={item.problem}
      meta={[
        { label: 'Scope', value: item.scope_label },
        { label: 'For', value: item.audience_label },
        { label: 'Setup', value: item.setup_label },
      ]}
      right={right}
      arrow
      onClick={onOpen}
    />
  );
};

// Work named to you in another company (client work you deliver) says whose it
// is, on the row and in the badge. Without that it reads as your own company's
// project, which is the one thing it is not.
const DeliveryRow: React.FC<{ request: AaiRequest; onOpen: () => void }> = ({ request, onOpen }) => {
  const meta: RowMeta[] = [
    { label: 'Owner', value: ownerOf(request), foot: request.department || 'Whole company' },
    { label: 'Next', value: nextFor(request) },
  ];
  if (request.company_name) meta.unshift({ label: 'Client', value: request.company_name, foot: 'You deliver this' });
  return (
    <ListRow
      mark={<span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">D</span>}
      title={request.title}
      badge={(
        <>
          <StatusChip status={stateFor(request)} />
          {request.company_name && <Chip tone="private">Client work</Chip>}
        </>
      )}
      desc={request.detail}
      meta={meta}
      arrow
      onClick={onOpen}
    />
  );
};

const ProjectsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'all';
  const openParam = params.get('open');
  const { context } = useParticipant();
  const [filter, setFilter] = useState<WorkFilter>('active');
  // Whatever the reader opened, held here so closing returns them to this list.
  const [openWin, setOpenWin] = useState<QuickWin | null>(null);
  const [openRequest, setOpenRequest] = useState<AaiRequest | null>(null);
  const wins = useAsync<QuickWinList>(() => aaiApi.getMyQuickWins(), 'Could not load your Quick Wins.');
  const requests = useAsync<RequestList>(() => aaiApi.listRequests(), 'Could not load your projects.');

  const all = useMemo(
    () => [...(requests.data?.requests || []), ...(requests.data?.assigned_elsewhere || [])],
    [requests.data],
  );
  /* Work named to you in a client company lives under Client work only, so
     the same piece of work is never listed twice. */
  const own = useMemo(() => requests.data?.requests || [], [requests.data]);
  const clientAsks = notBuiltYet(requests.data?.assigned_elsewhere || []);
  const rows = useMemo(() => own.filter(MATCH[filter]), [own, filter]);

  // An Activity link lands here with the project it names already open.
  useEffect(() => {
    if (!openParam || all.length === 0) return;
    const found = all.find((r) => r.id === openParam) || null;
    setOpenRequest(found);
  }, [openParam, all]);

  const setTab = (id: string) => setParams({ tab: id }, { replace: true });

  /* Client work: the projects this person is named on in somebody else's
     company. Only a delivery expert has any, so only they are shown the tab. */
  const isExpert = Boolean(context?.is_expert) || localStorage.getItem('is_expert') === '1';

  /* The tab needs its own count, and the panel that knows the number only
     renders once the tab is open, so the tab would have sat blank next to three
     siblings carrying numbers. Same roster call the panel makes; the browser
     serves the second one from cache. */
  const [clientWork, setClientWork] = useState<number | null>(null);
  useEffect(() => {
    if (!isExpert) return;
    let alive = true;
    getProjectsRoster()
      .then((r: any) => { if (alive) setClientWork((r?.projects || []).length); })
      .catch(() => { if (alive) setClientWork(null); });
    return () => { alive = false; };
  }, [isExpert]);

  if (wins.loading || requests.loading) return <PageBody><Loading label="Loading your projects" /></PageBody>;
  if (requests.error || !requests.data) return <PageBody><ErrorNote message={requests.error || 'Something went wrong.'} /></PageBody>;

  const items = wins.data?.items || [];
  const deliveries = own.filter(isDelivery);
  const showQuick = tab === 'all' || tab === 'quick_wins';
  const showDelivery = tab === 'all' || tab === 'delivery';
  // Skills you wrote yourself are neither a company Quick Win nor a Delivery
  // Project: they are yours until you offer them, so they carry their own tab
  // and appear under All beneath the work that was approved for you.
  const showMine = tab === 'all' || tab === 'my_skills';
  /* Client work sits before Your own skills because it is still other people's
     approved work, and the skills tab is the one thing on this page that is
     nobody's but yours. It was a nav row of its own, which made the sidebar ask
     "which Projects?" before the page could answer it. */

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow={context?.department || 'My work'}
        title="My Projects"
        sub="Every approved piece of work is a Project. Orbit recommends a Quick Win for a focused result or a Delivery Project when the work needs scoped delivery, a named team, and coordinated execution."
        actions={askOrbitAction()}
      />

      <Tabs
        tabs={[
          { id: 'all', label: 'All projects', count: items.length + deliveries.length },
          { id: 'quick_wins', label: 'Quick Wins', count: items.length },
          { id: 'delivery', label: 'Delivery Projects', count: deliveries.length },
          ...(isExpert ? [{ id: 'client_work', label: 'Client work', count: clientWork === null ? undefined : clientWork + clientAsks.length }] : []),
          { id: 'my_skills', label: 'Your own skills' },
        ]}
        active={tab}
        onChange={setTab}
        label="Project type"
      />

      {tab === 'client_work' && isExpert && (
        <div className="space-y-5">
          <ClientAgentsPage />
          {clientAsks.length > 0 && (
            <Panel>
              <div className="px-5 pt-4 pb-2 md:px-6">
                <div className="text-[13px] font-semibold text-ink">Client requests named to you</div>
                <div className="text-[12px] text-ink-dim">Not built yet. Each one moves up to the projects above once its build starts.</div>
              </div>
              <ClientRequests rows={clientAsks} showClient onOpen={setOpenRequest} />
            </Panel>
          )}
        </div>
      )}

      {showQuick && (
        <Panel className="mb-6">
          <PanelHead
            kicker="Project type · Quick Win"
            title="Focused projects ready for you"
            desc="A focused result with limited setup, used and reviewed directly in the portal."
            action={tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('quick_wins')}>See all Quick Wins</Action>}
          />
          {wins.error && <div className="px-6 py-5"><ErrorNote message={wins.error} /></div>}
          {!wins.error && items.length === 0 && (
            <EmptyState
              title="No Quick Wins are available to you yet"
              reason="A Quick Win appears here once it is approved for you or your department. You can ask for one and it routes to your department head."
              action={<Action size="sm" arrow to="/orbit/request">Ask for a Quick Win</Action>}
            />
          )}
          <Divided>
            {items.map((item) => <QuickWinRow key={item.id} item={item} onOpen={() => setOpenWin(item)} />)}
          </Divided>
        </Panel>
      )}

      {showDelivery && (
        <>
          <div className="mb-3.5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Project type · Delivery Project</div>
              <h2 className="m-0 text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink">Planned, quoted, and delivered with a named team</h2>
              <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">A larger change that needs shared ownership, scoped delivery, coordinated work, and governed release.</p>
            </div>
            {tab === 'all' && <Action size="sm" variant="quiet" onClick={() => setTab('delivery')}>See all Delivery Projects</Action>}
          </div>

          <Panel>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-6">
              <FilterChips tabs={FILTERS} active={filter} onChange={(id) => setFilter(id as WorkFilter)} label="Project filter" />
              <span className="text-[12px] text-ink-soft">{rows.length} in this view</span>
            </div>
            {rows.length === 0 && (
              <EmptyState
                title="Nothing in this view"
                reason="Projects you raise or are named on appear here. Nothing matches this filter right now."
                action={<Action size="sm" arrow to="/orbit/request">Raise a request</Action>}
              />
            )}
            <Divided>
              {rows.map((r) => <DeliveryRow key={r.id} request={r} onOpen={() => setOpenRequest(r)} />)}
            </Divided>
            <div className="flex items-center gap-2.5 px-5 py-4 text-[12.5px] leading-[19px] text-ink-dim md:px-6">
              <Chip tone="private" dot>Named members only</Chip>
              Project summary, Workspace membership and source access are three separate permissions. Delivery detail opens in Workspace for named members.
            </div>
          </Panel>
        </>
      )}

      {showMine && <MySkillsSection className={showDelivery ? 'mt-6' : ''} />}

      {openWin && <QuickWinDrawer item={openWin} onClose={() => setOpenWin(null)} />}
      {openRequest && <RequestDrawer request={openRequest} onClose={() => setOpenRequest(null)} />}
    </PageBody>
  );
};

export default ProjectsPage;
